import {
  Dispatch,
  FC,
  SetStateAction,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PrimaryInput, PrimaryCheckbox } from '.';
import {
  DEFAULT_CLOSE_TIME,
  DEFAULT_OPEN_TIME,
  defaultHours,
  mapDayOfWeek,
} from '../../../constants';
import { IBusinessHours, IHours, IOpenStatus } from '../../../types';

interface IBusinessComponent {
  business?: IBusinessHours;
  setBusiness: Dispatch<SetStateAction<IBusinessHours>>;
  edit?: boolean;
  onValidityChange?: (valid: boolean) => void;
}

type TouchedDay = { openTime: boolean; closeTime: boolean };

const emptyTouched = (): Record<number, TouchedDay> =>
  defaultHours.reduce(
    (acc, d) => ({
      ...acc,
      [d.weekday]: { openTime: false, closeTime: false },
    }),
    {} as Record<number, TouchedDay>,
  );

const fillWeek = (input: IHours[] | undefined): IHours[] => {
  const byDay = new Map((input ?? []).map((h) => [h.weekday, h]));
  return defaultHours.map((d) => byDay.get(d.weekday) ?? d);
};

export const BusinessHours: FC<IBusinessComponent> = ({
  business,
  setBusiness,
  edit,
  onValidityChange,
}) => {
  const [hours, setHours] = useState<IHours[]>(defaultHours);
  const [payload, setPayload] = useState<IBusinessHours>({
    openState: 'OPEN',
    hours: [],
  });
  const [touchedDays, setTouchedDays] =
    useState<Record<number, TouchedDay>>(emptyTouched);
  const pushedUpRef = useRef(false);

  const openStatus: IOpenStatus[] = ['OPEN', 'CLOSED', 'HOURS'];

  const applyHours = (nextHours: IHours[]) => {
    setHours(nextHours);
    if (payload.openState === 'HOURS') {
      const next: IBusinessHours = { openState: 'HOURS', hours: nextHours };
      setPayload(next);
      pushedUpRef.current = true;
      setBusiness(next);
    }
  };

  const touchDay = (weekday: number) => {
    setTouchedDays((prev) => ({
      ...prev,
      [weekday]: { openTime: true, closeTime: true },
    }));
  };

  const markFieldTouched = (
    weekday: number,
    field: 'openTime' | 'closeTime',
  ) => {
    setTouchedDays((prev) => ({
      ...prev,
      [weekday]: { ...prev[weekday], [field]: true },
    }));
  };

  const onGetInputValue = (
    event: SyntheticEvent<HTMLInputElement>,
    weekday: number,
  ) => {
    const value = event.currentTarget.value;
    const name = event.currentTarget.name as 'openTime' | 'closeTime';
    markFieldTouched(weekday, name);
    const nextHours = hours.map((h) =>
      h.weekday === weekday ? { ...h, [name]: value } : h,
    );
    applyHours(nextHours);
  };

  const setOpenStatus = (status: IOpenStatus) => {
    const next: IBusinessHours =
      status === 'HOURS'
        ? { openState: 'HOURS', hours }
        : { openState: status, hours: [] };
    setPayload(next);
    pushedUpRef.current = true;
    setBusiness(next);
  };

  const handleDay = (weekday: number, status: 'Open' | 'Closed') => {
    const nextHours = hours.map((h) => {
      if (h.weekday !== weekday) return h;
      if (status === 'Closed') return { weekday, isOpen: false };
      // Restore 8am–10pm defaults when reopening a previously closed day so
      // the inputs are never empty.
      return {
        ...h,
        isOpen: true,
        openTime: h.openTime ?? DEFAULT_OPEN_TIME,
        closeTime: h.closeTime ?? DEFAULT_CLOSE_TIME,
      };
    });
    if (status === 'Open') touchDay(weekday);
    applyHours(nextHours);
  };

  const setWholeDay = (
    e: SyntheticEvent<HTMLInputElement>,
    weekday: number,
  ) => {
    const checked = e.currentTarget.checked;
    const nextHours = hours.map((h) => {
      if (h.weekday !== weekday) return h;
      return checked
        ? { weekday, isOpen: true, wholeDay: true }
        : { ...h, wholeDay: false };
    });
    touchDay(weekday);
    applyHours(nextHours);
  };

  useEffect(() => {
    if (!edit || !business || pushedUpRef.current) return;
    const nextOpenState: IOpenStatus = business.openState ?? 'OPEN';
    const nextHours = fillWeek(business.hours);
    setHours(nextHours);
    setPayload({
      openState: nextOpenState,
      hours: nextOpenState === 'HOURS' ? nextHours : [],
    });
  }, [edit, business]);

  const isValid = useMemo(() => {
    if (payload.openState !== 'HOURS') return true;
    return hours.every(
      (d) => !d.isOpen || d.wholeDay || (!!d.openTime && !!d.closeTime),
    );
  }, [payload.openState, hours]);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const isDayInError = (index: number): boolean => {
    const day = hours[index];
    if (!day?.isOpen || day.wholeDay) return false;
    const hasBothTimes = !!day.openTime && !!day.closeTime;
    if (hasBothTimes) return false;
    const touched = touchedDays[day.weekday];
    return !!touched && (touched.openTime || touched.closeTime);
  };

  return (
    <div className="mt-10">
      <p className="semibold text-base text-text">Business Hours</p>
      <p className="mt-2 text-sm text-text-placeholder">
        Provide information about when they operate
      </p>

      <div className="inline-flex items-center rounded-lg border border-slate-medium bg-subtle p-1 mt-4">
        {openStatus.map((status) => {
          const active = payload.openState === status;
          const label =
            status.charAt(0) + status.slice(1).toLowerCase();
          return (
            <button
              key={status}
              type="button"
              onClick={() => setOpenStatus(status)}
              className={
                active
                  ? 'px-4 py-2 text-sm medium rounded-md bg-primary text-white shadow-sm'
                  : 'px-4 py-2 text-sm medium rounded-md text-text-placeholder'
              }
            >
              {label}
            </button>
          );
        })}
      </div>

      {payload.openState === 'HOURS' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
          {hours.map(({ weekday }, index) => (
            <div
              key={weekday}
              className="rounded-lg border border-slate-medium bg-subtle p-4"
            >
              <div className="flex w-full items-center justify-between">
                <div className="medium text-sm text-text">
                  {mapDayOfWeek[weekday as keyof typeof mapDayOfWeek]}
                </div>
                <div className="inline-flex items-center rounded-md border border-slate-medium bg-white p-1">
                  {(['Closed', 'Open'] as const).map((status) => {
                    const active =
                      (hours[index].isOpen && status === 'Open') ||
                      (!hours[index].isOpen && status === 'Closed');
                    return (
                      <button
                        key={status}
                        type="button"
                        onClick={() => handleDay(weekday, status)}
                        className={
                          active
                            ? 'px-3 py-1 text-xs medium rounded bg-primary text-white'
                            : 'px-3 py-1 text-xs medium rounded text-text-placeholder'
                        }
                      >
                        {status}
                      </button>
                    );
                  })}
                </div>
              </div>
              {hours[index].isOpen && (
                <div className="flex flex-col w-full mt-3">
                  {!hours[index].wholeDay && (
                    <>
                      <div className="flex items-end w-full">
                        <PrimaryInput
                          onChange={(e) => onGetInputValue(e, weekday)}
                          placeholder="Select time"
                          name="openTime"
                          label="Opening Time"
                          className="w-5/12"
                          type="time"
                          value={hours[index].openTime}
                        />
                        <div className="px-2 w-2/12">
                          <div className="w-full border-b border-slate-medium mb-4" />
                        </div>
                        <PrimaryInput
                          onChange={(e) => onGetInputValue(e, weekday)}
                          placeholder="Select time"
                          name="closeTime"
                          label="Closing Time"
                          className="w-5/12"
                          type="time"
                          value={hours[index].closeTime}
                        />
                      </div>
                      {isDayInError(index) && (
                        <p className="text-primary text-xs mt-1">
                          Enter both opening and closing times
                        </p>
                      )}
                    </>
                  )}
                  <PrimaryCheckbox
                    label="Open 24 Hours ?"
                    name="wholeDay"
                    checked={hours[index]?.wholeDay || false}
                    onChange={(e) => setWholeDay(e, weekday)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
